import { buildSurveySubmitBodyFromStorage, submitCustomerSurvey } from '@/api/customerSurvey';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2Complete } from '@/lib/q7_2Storage';
import { isQ7_3Complete } from '@/lib/q7_3Storage';
import { isQ7_4CompleteForSurvey } from '@/lib/q7_4Storage';
import { isDevRegComplete } from '@/lib/devregStorage';
import { isSwitchComplete } from '@/lib/switchStorage';
import { isSwitchReasonComplete } from '@/lib/switchReasonStorage';
import { isNpdAwarenessComplete } from '@/lib/npdAwarenessStorage';
import { readTradeInAnswers } from '@/lib/tradeinStorage';
import {
  readTradeInContactAnswers,
  writeTradeInContactAnswers,
} from '@/lib/tradeinContactStorage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import { selectPdpaConsent, selectSurveyScreening, selectUniqueCodeDraft } from '@/stores/selectors';
import { formatLocalDigits, parseLocalMobileInput } from '@/utils/malaysiaMobile';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300';

const mobileFieldShell =
  'flex min-w-0 w-full overflow-hidden rounded-sm border border-slate-300/90 bg-white focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-300';

const mobilePrefixClass =
  'flex shrink-0 items-center border-r border-slate-300/90 bg-slate-100 px-2.5 py-2.5 text-[16px] font-semibold tabular-nums text-slate-800';

const mobileLocalInputClass =
  'min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none focus:ring-0 tabular-nums';

export default function TradeInContact() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits: draftMobile, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const stored = readTradeInContactAnswers();
  const [mobileLocalDigits, setMobileLocalDigits] = useState(
    () => stored?.mobileLocalDigits ?? draftMobile
  );
  const [name, setName] = useState(() => stored?.name ?? '');
  const [email, setEmail] = useState(() => stored?.email ?? '');
  const [warning, setWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    setWarning(null);
    const digits = mobileLocalDigits.replace(/\D/g, '');
    if (digits.length === 0) {
      setWarning(t.tradeInContact.mobileWarning);
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) {
      setWarning(t.tradeInContact.nameWarning);
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setWarning(t.tradeInContact.emailWarning);
      return;
    }
    if (!customerUuid) {
      setWarning(t.tradeInContact.sessionExpiredWarning);
      return;
    }

    const body = buildSurveySubmitBodyFromStorage({
      name: trimmedName,
      email: trimmedEmail,
      mobileLocalDigits: digits,
    });
    if (!body) {
      setWarning(t.tradeInContact.incompleteWarning);
      return;
    }

    setIsSubmitting(true);
    writeTradeInContactAnswers({
      mobileLocalDigits: digits,
      name: trimmedName,
      email: trimmedEmail,
    });

    void submitCustomerSurvey(customerUuid, body)
      .then((tng) => {
        if (tng != null) {
          writeTngRewardSession({
            pin: tng.pin,
            expirydate: tng.expirydate,
            value: tng.value,
          });
          clearAllLocalStorage();
        }
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.tradeInContact.saveError);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!draftMobile || draftMobile.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2Complete()) {
    return <Navigate to="/7_2" replace />;
  }
  if (!isQ7_3Complete()) {
    return <Navigate to="/7_3" replace />;
  }
  if (!isQ7_4CompleteForSurvey()) {
    return <Navigate to="/7_4" replace />;
  }
  if (!isDevRegComplete()) {
    return <Navigate to="/devreg" replace />;
  }
  if (!isSwitchComplete()) {
    return <Navigate to="/switch" replace />;
  }
  if (!isSwitchReasonComplete()) {
    return <Navigate to="/switch-reason" replace />;
  }
  if (!isNpdAwarenessComplete()) {
    return <Navigate to="/npd-awareness" replace />;
  }
  if (readTradeInAnswers()?.tradeIn !== 'yes_help') {
    return <Navigate to="/tradein" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <SurveyBrownPanel className="!p-4">
              <p className="m-0 text-left text-[16px] font-medium leading-snug text-white/95 sm:text-[17px]">
                {t.tradeInContact.instruction}
              </p>
              <div className="mt-5 flex flex-col gap-4">
                <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-4 sm:gap-x-3">
                  <label
                    htmlFor="tradein-mobile"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.tradeInContact.mobileLabel}
                  </label>
                  <div className={mobileFieldShell}>
                    <span className={mobilePrefixClass} aria-hidden>
                      +60
                    </span>
                    <input
                      id="tradein-mobile"
                      type="tel"
                      name="mobile"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="12 345 6789"
                      value={formatLocalDigits(mobileLocalDigits)}
                      onChange={(e) =>
                        setMobileLocalDigits(parseLocalMobileInput(e.target.value))
                      }
                      className={mobileLocalInputClass}
                    />
                  </div>
                  <label
                    htmlFor="tradein-name"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.tradeInContact.nameLabel}
                  </label>
                  <input
                    id="tradein-name"
                    type="text"
                    name="name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={lightInputClassName}
                  />
                  <label
                    htmlFor="tradein-email"
                    className="text-left text-[16px] font-medium text-white/95 sm:text-[17px]"
                  >
                    {t.tradeInContact.emailLabel}
                  </label>
                  <input
                    id="tradein-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={lightInputClassName}
                  />
                </div>
              </div>
              {warning != null ? (
                <div className="mt-4">
                  <SurveyValidationAlert>{warning}</SurveyValidationAlert>
                </div>
              ) : null}
              <div className="mt-8 flex justify-center">
                <MetallicPillButton
                  type="button"
                  size="sm"
                  className="metallic-pill--lang-home"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                >
                  {t.tradeInContact.submit}
                </MetallicPillButton>
              </div>
              <p className="mt-6 m-0 text-left text-[13px] italic leading-snug text-white/80 sm:text-[14px]">
                {t.tradeInContact.pdpaNote}
              </p>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
